// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { BadgeImagePlacementValues, BadgeModel, BadgeTypeValues } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { OverlayBadgeData } from "@monsma/XDL/types";
import { useRef } from "react";

interface Props {
    productContext: ProductContextModel;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    maximumNumberOfOverlayBadges?: number;
    styles?: { badgeTextWrapper?: InjectableCss; badgeImageWrapper?: InjectableCss };
}

const getBadgePositioning = (overlayBadge: BadgeModel) => {
    const width = overlayBadge.badgeType === BadgeTypeValues.Image ? 25 : 50;
    const isImageBadge = overlayBadge.badgeType === BadgeTypeValues.Image;

    switch (overlayBadge.largeImagePlacement) {
        case BadgeImagePlacementValues.TopLeft:
            return "left: 2%; top: 2%;";

        case BadgeImagePlacementValues.TopCenter:
            return isImageBadge ? `left: ${(100 - width) / 2}%; top: 2%;` : "left: 2%; top: 2%; width: 96%;";

        case BadgeImagePlacementValues.TopRight:
            return "right: 2%; top: 2%;";

        case BadgeImagePlacementValues.BottomLeft:
            return "left: 2%; bottom: 2%;";

        case BadgeImagePlacementValues.BottomCenter:
            return isImageBadge ? `left: ${(100 - width) / 2}%; bottom: 2%;` : "left: 2%; bottom: 2%; width: 96%;";

        case BadgeImagePlacementValues.BottomRight:
            return "right: 2%; bottom: 2%;";

        default:
            return "left: 2%; top: 2%;";
    }
};

// Returns one badge per position
const getBadgesPerPosition = (badges: BadgeModel[], maximumNumberOfOverlayBadges?: number): BadgeModel[] => {
    const badgesPerPosition = badges.reduce((result, badge) => {
        const position =
            badge.largeImagePlacement === BadgeImagePlacementValues.None
                ? BadgeImagePlacementValues.TopLeft
                : badge.largeImagePlacement;

        if (!position) {
            return result;
        }

        const existingBadge = result.get(position);

        if (!existingBadge) {
            result.set(position, badge);
            return result;
        }

        if (
            badge.badgeType === BadgeTypeValues.Image &&
            existingBadge.badgeType === BadgeTypeValues.Text &&
            badge.sortOrder === existingBadge.sortOrder
        ) {
            result.set(position, badge);
        }

        return result;
    }, new Map<string, BadgeModel>());

    const overlayBadges = Array.from(badgesPerPosition.values());

    return maximumNumberOfOverlayBadges ? overlayBadges.slice(0, maximumNumberOfOverlayBadges) : overlayBadges;
};

/**
 * Manages the positioning for badges that overlay a product image.
 * @param props
 */
const useOverlayBadge = ({
    productContext,
    showTextBadge,
    showImageBadge,
    maximumNumberOfOverlayBadges = 1,
    styles,
}: Props) => {
    const badgeAreaWrapperRef = useRef<Record<number, HTMLDivElement | null>>({});

    const overlayBadgeWrapperClickHandler = (index: number) => {
        const badgeWrapper = badgeAreaWrapperRef.current[index];

        if (badgeWrapper) {
            (badgeWrapper.firstChild as HTMLLinkElement)?.click();
        }
    };

    let visibleBadges = productContext.product.badges?.filter(badge => badge.displayOnProductImages) || [];

    if (!showImageBadge && visibleBadges.length > 0) {
        visibleBadges = visibleBadges.filter(badge => badge.badgeType !== BadgeTypeValues.Image);
    }

    if (!showTextBadge && visibleBadges.length > 0) {
        visibleBadges = visibleBadges.filter(badge => badge.badgeType !== BadgeTypeValues.Text);
    }

    const selectedBadges = getBadgesPerPosition(visibleBadges, maximumNumberOfOverlayBadges);

    const overlayBadges: OverlayBadgeData[] = selectedBadges.map((overlayBadge, index) => {
        let overlayBadgeWrapperStyles =
            overlayBadge.badgeType === BadgeTypeValues.Image ? styles?.badgeImageWrapper : styles?.badgeTextWrapper;

        const positioning = getBadgePositioning(overlayBadge);

        if (overlayBadgeWrapperStyles?.css) {
            overlayBadgeWrapperStyles = {
                ...overlayBadgeWrapperStyles,
                css: overlayBadgeWrapperStyles.css.concat(positioning),
            };
        }

        return {
            overlayBadge,
            overlayBadgeWrapperStyles,
            badgeAreaWrapperRef,
            overlayBadgeWrapperClickHandler: () => overlayBadgeWrapperClickHandler(index),
        };
    });

    return {
        overlayBadges,
    };
};

export default useOverlayBadge;
